import { trpcBrowserClient } from "@/lib/trpc/browser";

const POLL_INTERVAL_MS = 1500;
// Mux typically assigns an asset within a few seconds of the upload
// completing — this bounds the wait to ~30s before surfacing an error.
const MAX_POLL_ATTEMPTS = 20;

/**
 * Uploads a recorded/attached video straight to Mux and creates its `Video`
 * row (source: MESSAGE) — the member-facing counterpart to the admin video
 * library's Mux upload flow (TRA-2435 follow-up: recorded/attached videos
 * used to be saved as raw files, which some viewers' browsers/extensions
 * silently failed to load).
 *
 * The Mux asset isn't transcoded yet when this resolves — the caller gets a
 * `videoId` whose `status` starts `CREATED`; the existing Mux webhook flips
 * it to `READY` once processing finishes, same as any library upload.
 */
export async function uploadVideoToMux(
  file: Blob,
  { filename, mimeType }: { filename: string; mimeType: string },
): Promise<{ videoId: string }> {
  const { id: uploadId, url } =
    await trpcBrowserClient.videos.mux.createMemberUpload.mutate();

  // Mux direct-upload URLs are backed by GCS's resumable-upload protocol,
  // which @mux/upchunk (used by the admin library's MuxUploader) satisfies by
  // sending every chunk with a `Content-Range: bytes {start}-{end}/{total}`
  // header. This single PUT is the one-chunk-covers-the-whole-file case of
  // that same protocol — the header still has to be there, or Mux has
  // nothing telling it where these bytes belong or that the upload is done.
  const putRes = await fetch(url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": mimeType,
      "Content-Range": `bytes 0-${file.size - 1}/${file.size}`,
    },
  });
  if (!putRes.ok) {
    throw new Error("Upload failed");
  }

  let assetId: string | null = null;
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    const { upload } = await trpcBrowserClient.videos.mux.getUpload.query({
      uploadId,
    });
    if (upload.asset_id) {
      assetId = upload.asset_id;
      break;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  if (!assetId) {
    throw new Error(
      "Upload finished but is still being processed by Mux — please try again in a moment",
    );
  }

  const video =
    await trpcBrowserClient.videos.mux.createFromMemberUpload.mutate({
      muxAssetId: assetId,
      filename,
      format: mimeType,
    });

  return { videoId: video.id };
}
